import React, { useState, useEffect } from 'react';
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg';
import Button from '../../composants/Button/Button';
import { useAuth } from '../../contexts/AuthContext';
import { isPrestataireRole, isPrestataireLibreRole } from '../../utils/roleUtils';
import './CreationComptesModal.css';

const NAME_REGEX = /^[\p{L}\s\-']+$/u;

const CreationComptesModal = ({ isOpen, onClose, onCreate }) => {
  const { user } = useAuth();
  const isPrestataire = user ? isPrestataireRole(user.role) : false;
  const isPL = user ? isPrestataireLibreRole(user.role) : false;
  const isGestionnaire = isPrestataire && !isPL;

  const [form, setForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    fonction: '',
    role: '',
  });
  const [errors, setErrors] = useState({ nom: '', prenom: '', email: '', telephone: '', role: '', api: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    if (isOpen) {
      setForm({ nom: '', prenom: '', email: '', telephone: '', fonction: '', role: '' });
      setErrors({ nom: '', prenom: '', email: '', telephone: '', role: '', api: '' });
      setIsSubmitting(false);
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone: string) => /^\d{10}$/.test(phone.replace(/\s/g, ''));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (errors.api) setErrors(prev => ({ ...prev, api: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    const newErrors = { nom: '', prenom: '', email: '', telephone: '', role: '', api: '' };

    if (!form.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
      hasErrors = true;
    } else if (!NAME_REGEX.test(form.nom.trim())) {
      newErrors.nom = 'Le nom ne doit contenir que des lettres, espaces, tirets ou apostrophes';
      hasErrors = true;
    }

    if (!form.prenom.trim()) {
      newErrors.prenom = 'Le prénom est requis';
      hasErrors = true;
    } else if (!NAME_REGEX.test(form.prenom.trim())) {
      newErrors.prenom = 'Le prénom ne doit contenir que des lettres, espaces, tirets ou apostrophes';
      hasErrors = true;
    }

    if (!validateEmail(form.email)) {
      newErrors.email = 'Email invalide';
      hasErrors = true;
    }

    if (!form.telephone.trim()) {
      newErrors.telephone = 'Le téléphone est requis';
      hasErrors = true;
    } else if (!validatePhone(form.telephone)) {
      newErrors.telephone = 'Le numéro doit comporter 10 chiffres';
      hasErrors = true;
    }

    if (!form.role) {
      newErrors.role = 'Le rôle est requis';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setErrors(newErrors); // Réinitialise les erreurs front avant l'appel API
    const lotsValides: string[] = [];
    setIsSubmitting(true);
    try {
      const result = await onCreate({ ...form, lots: lotsValides });
      if (result?.ok === false) {
        setErrors(prev => ({
          ...prev,
          ...(result.errors ?? {}),
          api: result.errors ? '' : (result.message ?? ''),
        }));
      } else {
        onClose();
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, api: 'Une erreur est survenue. Veuillez réessayer.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ display: 'flex' }} onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Créer un utilisateur</h2>
          <button type="button" className="close-button" onClick={onClose}>
            <CrossIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Prénom <span className="required-star">*</span></label>
                <input type="text" name="prenom" value={form.prenom} onChange={handleChange} className={errors.prenom ? 'input-error' : ''} placeholder="Marie" />
                {errors.prenom && <span className="error-message">{errors.prenom}</span>}
              </div>
              <div className="form-group">
                <label>Nom <span className="required-star">*</span></label>
                <input type="text" name="nom" value={form.nom} onChange={handleChange} className={errors.nom ? 'input-error' : ''} placeholder="Dupont" />
                {errors.nom && <span className="error-message">{errors.nom}</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Email de connexion <span className="required-star">*</span></label>
              <input type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} className={errors.email ? 'input-error' : ''} placeholder="email@exemple.fr" />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>Téléphone <span className="required-star">*</span></label>
              <input
                type="tel"
                name="telephone"
                value={form.telephone}
                onChange={(e) => setForm({ ...form, telephone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                className={errors.telephone ? 'input-error' : ''}
                placeholder="0612345678"
                maxLength={10}
              />
              {errors.telephone && <span className="error-message">{errors.telephone}</span>}
            </div>
            <div className="form-group">
              <label>Fonction</label>
              <input type="text" name="fonction" value={form.fonction} onChange={handleChange} placeholder="Ex: Acheteur public" />
            </div>
            <div className="form-group">
              <label>Rôle <span className="required-star">*</span></label>
              <select name="role" value={form.role} onChange={handleChange} className={errors.role ? 'input-error' : ''}>
                <option value="">Sélectionner un rôle</option>
                {isPrestataire ? (
                  <>
                    <option value="prestataire">Prestataire</option>
                    <option value="prestataire_signataire">Prestataire signataire</option>
                    {isGestionnaire && <option value="gestionnaire">Prestataire signataire admin</option>}
                    {isPL && <option value="prestataire_libre_admin">Prestataire libre admin</option>}
                  </>
                ) : (
                  <>
                    <option value="acheteur">Acheteur</option>
                    <option value="acheteur_signataire">Acheteur signataire</option>
                    <option value="responsable_achat">Acheteur signataire admin</option>
                    <option value="observateur" disabled style={{ color: '#999' }}>
                      Observateur, En cours de développement
                    </option>
                  </>
                )}
              </select>
              {errors.role && <span className="error-message">{errors.role}</span>}
            </div>
            <span style={{ fontSize: '1rem', color: '#6b7280', marginTop: '0.8rem', display: 'block' }}>
              Un lien valable 60 minutes sera envoyé par email à l'utilisateur pour définir son mot de passe. Il devra également vérifier son adresse email. Ces deux actions sont accessibles depuis la page de connexion.
            </span>
            {errors.api && (
              <span className="error-message" style={{ marginTop: '1rem', display: 'block' }}>{errors.api}</span>
            )}
          </div>
          <div className="modal-actions">
            <Button
              value="Annuler"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            />
            <Button
              value={isSubmitting ? 'Création...' : 'Créer'}
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreationComptesModal;